import moment from "moment"
import { scaleLinear, scaleTime } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import { axisBottom } from "d3-axis"
import { createSelector } from "reselect"
import { filter, isFinite, first, last, find } from "lodash"
import { calculateHighlighter } from "../helpers/highlighter"
import { xSmallAxisTicks } from "../helpers/x-axis-ticks"

const MARGINS = { top: 2, left: 0, right: 0, bottom: 2 }
const EMPTY_OBJECT = {}

const getDate = props => props.date
const getRawData = props => props.data
const getWidth = props => props.width || 150
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate
const getColor = props => props.color
const getHeight = props => props.height
export const getDriftValues = props => props.driftValues

export const getData = createSelector(
  [getRawData],
  data => filter(data, datum => isFinite(datum.value)),
)

const getMinX = createSelector(
  [getStartDate, getData],
  (startDate, data) => {
    if (data && data.length > 0) return moment(first(data).date).toDate()
    return moment(startDate).toDate()
  },
)

const getMaxX = createSelector(
  [getEndDate, getData],
  (endDate, data) => {
    if (data && data.length > 0) return moment(last(data).date).toDate()
    return moment(endDate).toDate()
  },
)

const getYValues = createSelector(
  [getData],
  (data) => data.map(datum => datum.value),
)

const getMinY = createSelector(
  [getYValues],
  (yValues) => Math.min(...yValues, 0),
)

const getMaxY = createSelector(
  [getYValues],
  (yValues) => Math.max(...yValues, 0),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([0, width - MARGINS.left - MARGINS.right]),
)

export const getScaleY = createSelector(
  [getMinY, getMaxY, getHeight],
  (minY, maxY, height) => scaleLinear()
    .domain([minY, maxY])
    .rangeRound([height - MARGINS.top, MARGINS.bottom]),
)

export const getLine = createSelector(
  [getScaleX, getScaleY, getData, getColor],
  (scaleX, scaleY, data, color) => ({
    d: line()
      .x(d => scaleX(d.date))
      .y(d => scaleY(d.value))
      .curve(curveLinear)(data),
    transform: `translate(${MARGINS.left},${MARGINS.top - MARGINS.bottom})`,
    style: { stroke: color },
  }),
)

export const getZero = createSelector(
  [getScaleY],
  (scaleY) => scaleY(0),
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getScaleY, getMinX, getMaxX, getMinY, getMaxY],
  calculateHighlighter,
)

export const getSelected = createSelector(
  [getDate, getData],
  (date, data) => find(data, { date: moment(date).toDate() }) || EMPTY_OBJECT,
)

export const getXTicks = createSelector(
  [getMinX, getMaxX],
  xSmallAxisTicks,
)

export const getXAxis = createSelector(
  [getWidth, getScaleX, getZero, getXTicks],
  (width, scaleX, zero, [ticks, formatter]) => ({
    fn: axisBottom(scaleX)
      .ticks(ticks)
      .tickFormat(formatter)
      .tickSize(-6),
    transform: `translate(0,${zero})`,
    baseline: {
      x1: MARGINS.left,
      x2: width - MARGINS.right,
      y1: zero,
      y2: zero,
    },
  }),
)
